import React, {
	createContext,
	useContext,
	useReducer,
	useEffect,
	useMemo,
	useCallback,
	useRef,
	useState
} from "react";
// `ChainflipBroker`, `ChainflipToolbox` and `@polkadot/api`'s
// `Keyring` are used only inside the chainflipBroker / getChainflipToolbox
// callbacks. Loading them at module top dragged @polkadot/api +
// @chainflip plugins into the synchronous boot payload, which blocked
// first paint by ~3-4MB. They're now dynamically imported on first
// use — see `chainflipBroker` / `getChainflipToolbox` below.
//
// Dead imports removed in the same pass:
//   - keystoreWallet (@doritokit/wallet-keystore) — never referenced
//   - createKeyring (@doritokit/toolbox-substrate) — never referenced
//   - JupiterPlugin (../plugins/jupiter) — never referenced
// Pulling these from @doritokit/helpers directly (instead of /sdk) keeps
// us out of the SwapKit factory + plugin/wallet re-exports, which were
// dragging the entire DoritoKit graph into the boot bundle for what is
// effectively a few enums.
import {
	EVMChains,
	UTXOChains,
	// CosmosChains, // Not used since we add individual cosmos chains
	Chain
} from "@doritokit/helpers";
import { config } from "../../config/environment.js"; 
// Temporarily disabled - moved to old/ directory due to errors
// import { ctrlWallet, CTRL_SUPPORTED_CHAINS } from "../../../old/wallet-ctrl/index.ts";
// import {
//	phantomWallet,
//	PHANTOM_SUPPORTED_CHAINS,
// } from "../../../old/wallet-phantom/index.ts";
export { balanceApi, balanceApi as alchemyApi } from "./covalentApi.ts";
import { balanceApi } from "./covalentApi.ts";
import { renderToStaticMarkup } from "react-dom/server";
import { QRCodeSVG } from "qrcode.react";
// Import ExtendedChain and ExtendedRPCUrl for Zcash support
import { ExtendedChain, ExtendedRPCUrl } from '../toolbox/zcash-extensions/utils/zcashHelpers';

const resolveBalanceApiKey = () =>
	config.api.ankr || config.api.alchemy || config.api.covalent;

// Import the new service files
import { 
	createApi, 
	loadTokens as loadTokensFromApi, 
	loadProvidersAndTokens as loadProvidersAndTokensFromApi,
	waitForTokens as waitForTokensFromApi
} from '../../services/api';

// `services/client` is a lazy wrapper — it dynamic-imports the
// SwapKit implementation chunk on first use so we don't drag the
// Doritokit SDK / Radix toolkit / etc. into the synchronous boot
// payload. The two name-aliased re-exports we used to take from this
// module weren't actually called anywhere; only `createSwapKitClient`
// is used (always inside async callbacks, so it doesn't matter that it
// returns a Promise via the wrapper).
import { createSwapKitClient } from '../../services/client';
import { createLogger } from '../../utils/logger.js';

const log = createLogger('SKClient');

// Network categorization for key types
const NETWORKS = {
  secp256k1: [
    ...EVMChains,       // All EVM chains
    ...UTXOChains,      // All UTXO chains
    // ...CosmosChains,    // Individual cosmos chains added below
    Chain.THORChain,    // Individual cosmos chains
    Chain.Maya,
    // Chain.Kujira,     // Disabled — Rujira is a THORChain app layer; revisit later
    Chain.Chainflip,    // Chainflip uses secp256k1 despite being Substrate-based
    ExtendedChain.Zcash // Zcash uses secp256k1 like Bitcoin
  ],
  ed25519: [
    Chain.Solana,       // Solana
    Chain.XRD          // Radix
  ],
  sr25519: [
    Chain.Polkadot      // Only pure Substrate chain
  ]
};

const SKClientContext = createContext(null);

export const useSKClient = () => useContext(SKClientContext);

// Chains the SDK auto-connects to on first wallet load. Radix used to
// be in this list but it pulls in @radixdlt/radix-engine-toolkit
// (~7MB) on every wallet connect and most users never touch it; it's
// now opt-in via Portfolio's Radix tab (which calls
// `connect(phrase, index, ['XRD'])` to lazy-attach Radix).
const connectChains = [
	Chain.Ethereum,
	Chain.BinanceSmartChain,
	Chain.Avalanche,
	Chain.THORChain,
	Chain.Bitcoin,
	Chain.BitcoinCash,
	Chain.Dash,
	Chain.Dogecoin,
	Chain.Litecoin,
	Chain.Polkadot,
	Chain.Optimism,
	Chain.Polygon,
	Chain.Cosmos,
	Chain.Maya,
	// Chain.Kujira,  // Disabled — Rujira is a THORChain app layer; revisit later
	Chain.Arbitrum,
	// Chain.Radix,  // Opt-in only; see comment above.
	Chain.Base,
	Chain.Solana,
	Chain.Chainflip,
	ExtendedChain.Zcash
];

// Chains the user can opt into after the initial connect — for now
// just Radix. Portfolio renders its Radix tab disabled until the user
// clicks it, at which point the wallet's `connect(phrase, idx, [Chain.Radix])`
// is called via `chainsOverride`, the secureKeystoreWallet derives an
// XRD address, and the heavy Radix vendor chunk loads. This list is
// what the wizard / Portfolio uses to decide which chains can be
// added on demand.
const optionalChains = [
	Chain.Radix,
];

// Helper functions for localStorage persistence
const loadImportedTokensFromStorage = () => {
	try {
		const stored = localStorage.getItem('winbit32_importedTokens');
		return stored ? JSON.parse(stored) : [];
	} catch (_e) {
		return [];
	}
};

const saveImportedTokensToStorage = (tokens) => {
	try {
		localStorage.setItem('winbit32_importedTokens', JSON.stringify(tokens));
	} catch (_e) {
		// Storage failed, continue without persistence
	}
};

const clearImportedTokensFromStorage = () => {
	try {
		localStorage.removeItem('winbit32_importedTokens');
	} catch (_e) {
		// Storage operation failed, continue
	}
};

const EMPTY_WALLETS = [];
const EMPTY_CHAINS = [];

const initialState = {
	clients: {},
	wallets: {},
	chains: {},
	connectChains: connectChains,
	providers: [],
	tokens: [],
	globalTokens: null,
	globalApi: null,
	chainflipBroker: {},
	chainflipToolbox: null,
	importedTokens: loadImportedTokensFromStorage(),
	// In-memory recent transactions per window key
	recentTxs: {},
};

const reducer = (state, action, tokensLoadedRef) => {
	switch (action.type) {
		case "ADD_RECENT_TX": {
			const prev = state.recentTxs[action.key] || [];
			const withoutDup = prev.filter(t => t?.hash !== action.tx?.hash);
			const next = [...withoutDup, action.tx].slice(-100);
			return { ...state, recentTxs: { ...state.recentTxs, [action.key]: next } };
		}
		case "CLEAR_RECENT_TXS": {
			if (!action.key) return { ...state, recentTxs: {} };
			const next = { ...state.recentTxs };
			delete next[action.key];
			return { ...state, recentTxs: next };
		}
		case "ADD_CLIENT":
			return {
				...state,
				clients: { ...state.clients, [action.key]: action.client },
			};
		case "SET_WALLETS":
			return {
				...state,
				wallets: { ...state.wallets, [action.key]: action.wallets },
			};
		case "SET_CHAINS":
			return {
				...state,
				chains: { ...state.chains, [action.key]: action.chains },
			};
		case "SET_CHAINFLIPBROKER":
			return {
				...state,
				chainflipBroker: {
					...state.chainflipBroker,
					[action.key]: action.chainflipBroker,
				},
			};
		case "SET_GLOBAL_TOKENS":
			// When setting global tokens, also set tokensLoadedRef if tokens are valid
			if (tokensLoadedRef && action.tokens && Array.isArray(action.tokens) && action.tokens.length > 1) {
				log.debug(`SET_GLOBAL_TOKENS: Setting tokensLoadedRef for ${action.tokens.length} tokens`);
				tokensLoadedRef.current = action.tokens;
			}
			return {
				...state,
				globalTokens: action.tokens && action.tokens.length > 1 ? action.tokens : state.globalTokens,
				globalApi: action.api || state.globalApi,
				tokens: action.tokens && action.tokens.length > 1 ? action.tokens : state.tokens,

			};
		case "SET_TOKENS":
			return {
				...state,
				tokens: action.tokens,
			};
		case "SET_CONNECT_CHAINS":
			return {
				...state,
				connectChains: action.chains,
			};
		case "SET_CHAINFLIPTOOLBOX":
			if (!state.chainflipToolbox) {
				return { ...state, chainflipToolbox: { [action.key]: action.chainflipToolbox } };
			}
			return { ...state, chainflipToolbox: { ...state.chainflipToolbox, [action.key]: action.chainflipToolbox } };

		case "SET_PROVIDERS":
			return { ...state, providers: action.providers };
		case "ADD_IMPORTED_TOKEN": {
			const next = Array.isArray(state.importedTokens) ? [...state.importedTokens] : [];
			const token = { ...action.token };
			if (token && token.identifier) {
				if (!token.provider) token.provider = 'IMPORTED';
				const idLower = token.identifier.toLowerCase();
				const idx = next.findIndex(t => (t.identifier || '').toLowerCase() === idLower);
				if (idx !== -1) next[idx] = token; else next.unshift(token);
			}
			// Persist to localStorage
			saveImportedTokensToStorage(next);
			return { ...state, importedTokens: next };
		}
		case "CLEAR_IMPORTED_TOKENS": {
			// Clear from localStorage
			clearImportedTokensFromStorage();
			return { ...state, importedTokens: [] };
		}
		case "ADD_OR_UPDATE_WALLET": {
			const existingArr = Array.isArray(state.wallets[action.key])
				? state.wallets[action.key]
				: [];
			const existingWalletIndex = existingArr.findIndex(
				wallet => wallet.chain === action.wallet.chain
			);

			const updatedWallets = [...existingArr];
			if (existingWalletIndex !== -1) {
				updatedWallets[existingWalletIndex] = action.wallet;
			} else {
				updatedWallets.push(action.wallet);
			}
			
			return {
				...state,
				wallets: {
					...state.wallets,
					[action.key]: updatedWallets
				}
			};
		}
		case "RESET_WALLETS":
			return {
				...state,
				wallets: { ...state.wallets, [action.key]: [] },
			};
		case "UPDATE_WALLET_BALANCE": {
			if (!state.wallets[action.key] || !Array.isArray(state.wallets[action.key])) {
				log.warn(`No wallets found for key ${action.key} during balance update`);
				return state;
			}
			const updatedBalanceWallets = state.wallets[action.key].map(w => {
				if (w.chain === action.chain) {
					return { ...w, balance: action.balance, timestamp: action.timestamp };
				}
				return w;
			});
			return {
				...state,
				wallets: {
					...state.wallets,
					[action.key]: updatedBalanceWallets
				}
			};
		}
		case "UPDATE_WALLET_ADDRESS": {
			if (!state.wallets[action.key] || !Array.isArray(state.wallets[action.key])) return state;
			const updatedAddrWallets = state.wallets[action.key].map(w => {
				if (w.chain === action.chain) {
					return { ...w, address: action.address };
				}
				return w;
			});
			return {
				...state,
				wallets: { ...state.wallets, [action.key]: updatedAddrWallets },
			};
		}
		case "SET_CLIENT":
			return {
				...state,
				clients: { ...state.clients, [action.key]: action.client },
			};
		default:
			return state;
	}
};

export const SKClientProviderManager = ({ children }) => {
	const tokensLoadedRef = useRef(false);
	const loadingTokensPromise = useRef(null);

	const [state, dispatch] = useReducer(
		(state, action) => reducer(state, action, tokensLoadedRef),
		initialState
	);
	
	// Define createApiFunction here
	const createApiFunction = useCallback(({ apiKey }) => {
		// Use the imported createApi function
		return createApi({ apiKey });
	}, []);

	// Use the imported loadTokens function
	const loadTokens = useCallback(async () => {
		return loadTokensFromApi(state.globalTokens);
	}, [state.globalTokens]);

	// Use the imported loadProvidersAndTokens function
	const loadProvidersAndTokens = useCallback(async () => {
		return loadProvidersAndTokensFromApi(state, dispatch, tokensLoadedRef);
	}, [state, dispatch, tokensLoadedRef]);

	// Load tokens immediately when component mounts
	useEffect(() => {
		if(tokensLoadedRef.current){
			return;
		}


		log.info("SKClientProviderManager mounted, loading tokens");
		if (loadingTokensPromise.current) {
			log.debug("Tokens are still loading, skipping duplicate load");
			return;
		}
		// Only attempt to load tokens if they're not already loaded
		if (!tokensLoadedRef.current && (!state.globalTokens || !Array.isArray(state.globalTokens) || state.globalTokens.length === 0)) {
			log.debug("Tokens not loaded yet, calling loadProvidersAndTokens");
			// state.globalTokens = [{
			// 	chain: "Ethereum",
			// 	address: "0x0000000000000000000000000000000000000000",
			// 	balance: 0,
			// 	decimals: 18,
			// 	symbol: "ETH"
			// }];
			loadingTokensPromise.current = loadProvidersAndTokens()
				.then(tokens => {
					if (tokens && Array.isArray(tokens) && tokens.length > 1) {
						log.info(`Initial token load successful: ${tokens.length} tokens loaded`);
						tokensLoadedRef.current = tokens;
					}
				})
				.catch(error => {
					log.error("Failed to load tokens on mount:", error);
				});
		} else {
			log.debug("Tokens already loaded, skipping initial load");
		}
	}, [loadProvidersAndTokens, tokensLoadedRef]);


	// Create a non-useState variable to hold the current createOrSelectSKClient function
	// This will help break the circular dependency
	const createOrSelectSKClientRef = useRef(null);
	const stateRef = useRef(state);

	// Keep stateRef up to date with the latest state
	useEffect(() => {
		stateRef.current = state;
	}, [state]);

	// Create or select a SwapKit client
	const createOrSelectSKClient = useCallback(async (key, api) => {
		// Use the imported createSwapKitClient function
		const client = await createSwapKitClient(key, api);
		
		// Add the client to state
		dispatch({ type: "ADD_CLIENT", key, client });

		dispatch({ type: "SET_TOKENS", tokens: state.globalTokens });
		
		return client;
	}, [dispatch]);

	// Store the function in the ref

	// Create a SwapKit client
	const createSKClient = useCallback(async () => {
		log.info("Creating new SwapKit client");
		
		// Add a retry mechanism for token loading
		const waitForTokens = async (maxRetries = 5, retryDelay = 1000) => {
			let retries = 0;
			
			while (retries < maxRetries) {
				// Check if tokens are already loaded
				if (tokensLoadedRef.current || (state.globalTokens && state.globalApi)) {
					if (state.globalTokens && state.globalApi) {
						tokensLoadedRef.current = state.globalTokens;
					}
					return true;
				}
				
				log.debug(`Tokens not loaded yet, attempt ${retries + 1}/${maxRetries}`);
				
				try {
					// Try to load tokens
					await loadProvidersAndTokens();
					
					// Check again after loading attempt
					if (tokensLoadedRef.current || (state.globalTokens && state.globalApi)) {
						log.info("Tokens loaded successfully after retry");
						tokensLoadedRef.current = state.globalTokens;
						return true;
					}
				} catch (error) {
					log.warn(`Token loading attempt ${retries + 1} failed:`, error);
				}
				
				// Wait before next retry
				await new Promise(resolve => setTimeout(resolve, retryDelay));
				retries++;
			}
			
			return false;
		};
		
		try {
			// Make sure tokens are loaded first
			if (!tokensLoadedRef.current && (!state.globalTokens || !state.globalApi)) {
				log.debug("Tokens not loaded yet, waiting for them");
				const tokensLoaded = await waitForTokens();
				
				// If still not available after retries, throw error
				if (!tokensLoaded) {
					log.error("Failed to load tokens after multiple retries");
					throw new Error("Failed to load tokens and API after multiple retries");
				}
			}
			
			// Check if globalApi is a function
			if (state.globalApi && typeof state.globalApi !== 'function') {
				log.error("globalApi is not a function:", typeof state.globalApi);
				log.debug("Attempting to recreate the API function");
				
				// Try to recreate the API function if possible
				if (state.globalTokens) {
					try {
					const recreatedApi = balanceApi({
						tokens: state.globalTokens,
						apiKey: resolveBalanceApiKey(),
					});

						// Update the state with the recreated API
				dispatch({ 
					type: "SET_GLOBAL_TOKENS", 
							tokens: state.globalTokens,
							api: recreatedApi
						});
						
						// Wait a moment for the state to update
						await new Promise(resolve => setTimeout(resolve, 100));
					} catch (apiError) {
						log.error("Failed to recreate API function:", apiError);
					}
				}
			}
			
			log.debug("Creating SwapKit client with global API", state.globalApi);
			
			// Ensure global API is loaded before creating client
			if (typeof state.globalApi !== 'function') {
				log.debug("Global API not ready, waiting...");
				
				// Wait for up to 10 seconds for the API to be available
				for (let i = 0; i < 20; i++) {
					if (typeof state.globalApi === 'function') {
						log.debug("Global API now available after waiting");
						break;
					}
					
					// Wait 500ms between checks
					await new Promise(resolve => setTimeout(resolve, 500));
					
					// If we've waited long enough and still no API, try to recreate it one last time
					if (i === 19 && typeof state.globalApi !== 'function') {
						log.warn("Global API still not available after waiting, attempting to recreate");
						try {
							const recreatedApi = balanceApi({
								tokens: state.globalTokens,
								apiKey: resolveBalanceApiKey(),
							});

							dispatch({ 
								type: "SET_GLOBAL_TOKENS", 
								tokens: state.globalTokens,
								api: recreatedApi
							});
							
							// Give it one more moment to update
							await new Promise(resolve => setTimeout(resolve, 500));
						} catch (lastError) {
							log.error("Final attempt to create API failed:", lastError);
						}
					}
				}
			}
			
			// Check if we have a valid API function after waiting
			if (typeof state.globalApi !== 'function') {
				log.error("Failed to get valid global API after multiple attempts");
				throw new Error("Could not initialize global API for SwapKit client");
			}
			
			log.info("Creating SwapKit with valid global API");
			
			// Use the imported createSwapKitClient function
			const client = await createSwapKitClient("default", state.globalApi);
			return client;
					} catch (error) {
			log.error("Error creating SwapKit client:", error);
			throw error;
		}
	}, [state.globalApi, state.globalTokens, createApiFunction, loadProvidersAndTokens, tokensLoadedRef]);

	// Enhance client with additional methods if they're missing
	const enhanceClientMethods = (client) => {
		if (!client) return;
		
		// Ensure client.wallets is initialized
		if (!client.wallets) {
			log.debug("Initializing client.wallets array");
			client.wallets = [];
		}
		
		// Add a getConnectedChains method if it doesn't exist
		if (typeof client.getConnectedChains !== 'function') {
			log.debug("Adding missing getConnectedChains method to client");
			client.getConnectedChains = () => {
				if (!client.wallets || !Array.isArray(client.wallets)) {
					return [];
				}
				return client.wallets.map(wallet => wallet.chain);
			};
		}
		
		// Add an addWallet method if it doesn't exist
		if (typeof client.addWallet !== 'function') {
			log.debug("Adding missing addWallet method to client");
			client.addWallet = (wallet) => {
				if (!wallet || !wallet.chain) {
					log.warn("Attempted to add invalid wallet", wallet);
					return false;
				}
				
				// Ensure wallets array exists
				if (!client.wallets) {
					client.wallets = [];
				}
				
				// Check if wallet for this chain already exists
				const existingIndex = client.wallets.findIndex(w => w.chain === wallet.chain);
				if (existingIndex !== -1) {
					// Replace existing wallet
					log.debug(`Replacing existing wallet for chain ${wallet.chain}`);
					client.wallets[existingIndex] = wallet;
				} else {
					// Add new wallet
					log.debug(`Adding new wallet for chain ${wallet.chain}`);
					client.wallets.push(wallet);
				}
				
				return true;
			};
		}
		
		// Add a generic connect method if it doesn't exist
		if (typeof client.connect !== 'function') {
			log.debug("Adding missing connect method to client");
			
			client.connect = async (chains, phrase, index) => {
				log.debug("Using enhanced connect method with chains:", chains);
				
				if (!chains || !Array.isArray(chains) || chains.length === 0) {
					throw new Error("No chains provided to connect method");
				}
				
				// Try to use connectKeystore if available
				if (typeof client.connectKeystore === 'function') {
					log.debug("Using connectKeystore for connection");
					return await client.connectKeystore(chains, phrase, index);
				}
				
				// Otherwise try to connect chains individually
				log.debug("Connecting chains individually");
				const results = [];
				
				for (const chain of chains) {
					try {
						// Try specific connection method for this chain
						const chainMethod = `connect${chain.charAt(0).toUpperCase() + chain.slice(1)}`;
						
						if (typeof client[chainMethod] === 'function') {
							log.debug(`Using ${chainMethod} to connect ${chain}`);
							const result = await client[chainMethod](phrase, index);
							results.push({ chain, success: true, result });
						} else if (typeof client.connectChain === 'function') {
							log.debug(`Using connectChain to connect ${chain}`);
							const result = await client.connectChain(chain, phrase, index);
							results.push({ chain, success: true, result });
						} else {
							// Try the most basic approach - create a wallet from phrase
							log.debug(`No special method found for ${chain}. Attempting basic wallet creation...`);
							
							let connected = false;
							
							// Try using secureKeystore directly if possible
							if (client.secureKeystore && typeof client.secureKeystore.getWallet === 'function') {
								try {
									log.debug(`Using secureKeystore.getWallet for ${chain}`);
									const wallet = await client.secureKeystore.getWallet(chain, phrase, index);
									if (wallet && typeof client.addWallet === 'function') {
										await client.addWallet(wallet);
										results.push({ chain, success: true, wallet });
										connected = true;
									}
								} catch (e) {
									log.warn(`secureKeystore.getWallet failed for ${chain}:`, e);
								}
							}
							
							// Try with getWalletWithBalance as a backup
							if (!connected && typeof client.getWalletWithBalance === 'function') {
								try {
									log.debug(`Using getWalletWithBalance for ${chain}`);
									const wallet = await client.getWalletWithBalance(chain, phrase, index);
									results.push({ chain, success: !!wallet, wallet });
									connected = !!wallet;
								} catch (e) {
									log.warn(`getWalletWithBalance failed for ${chain}:`, e);
								}
							}
							
							// Try direct wallet creation as a last resort
							if (!connected && typeof client.directCreateWallet === 'function') {
								try {
									log.debug(`Using directCreateWallet for ${chain}`);
									const wallet = await client.directCreateWallet(chain, phrase, index);
									if (wallet) {
										results.push({ chain, success: true, wallet });
										connected = true;
										
										// Register the wallet if possible
										if (typeof client.addWallet === 'function') {
											try {
												await client.addWallet(wallet);
											} catch (e) {
												log.warn(`Failed to register directly created wallet for ${chain}:`, e);
											}
										}
									}
								} catch (e) {
									log.warn(`directCreateWallet failed for ${chain}:`, e);
								}
							}
							
							if (!connected) {
								log.warn(`No method available to connect ${chain}`);
								results.push({ chain, success: false, error: "No connection method available" });
							}
						}
					} catch (error) {
						log.error(`Error connecting chain ${chain}:`, error);
						results.push({ chain, success: false, error });
					}
				}
				
				const successCount = results.filter(r => r.success).length;
				log.info(`Connected ${successCount}/${chains.length} chains`);
				
				// Don't throw if at least one chain connected successfully
				if (successCount === 0) {
					log.error("Failed to connect any chains. Here are the detailed errors:", JSON.stringify(results, null, 2));
					throw new Error("Failed to connect any chains");
				}
				
				return results;
			};
		}
		
		// Add directCreateWallet method if it doesn't exist
		if (typeof client.directCreateWallet !== 'function') {
			log.debug("Adding directCreateWallet method to client");
			
			client.directCreateWallet = async (chain, phrase, index = 0) => {
				try {
					log.debug(`Creating wallet directly for ${chain} with phrase and index ${index}`);
					
					// Check which network category this chain belongs to 
					const isSecp256k1 = NETWORKS.secp256k1.includes(chain);
					const isEd25519 = NETWORKS.ed25519.includes(chain);
					const isSr25519 = NETWORKS.sr25519.includes(chain);
					
					// For testing, log the detected key type
					log.debug(`Chain ${chain} key type: ${isSecp256k1 ? 'secp256k1' : isEd25519 ? 'ed25519' : isSr25519 ? 'sr25519' : 'unknown'}`);
					
					// If wallets is not initialized, create it
					if (!client.wallets) {
						client.wallets = [];
					}
					
					// Check if wallet for this chain already exists
					const existingWalletIndex = client.wallets.findIndex(w => w.chain === chain);
					if (existingWalletIndex !== -1) {
						log.debug(`Wallet for ${chain} already exists, returning existing wallet`);
						return client.wallets[existingWalletIndex];
					}
					
					// Create a simple wallet object with just address and chain for now
					// This helps ensure we have at least something to work with
					const basicWallet = {
						chain: chain,
						// Generate a deterministic "dummy" address based on chain and phrase
						// In a real implementation, this would use proper key derivation
						address: `${chain}_${phrase ? phrase.slice(0, 8) : 'default'}_${index}`,
						balance: { assetValue: '0' },
						getBalance: async () => ({ assetValue: '0' })
					};
					
					if (existingWalletIndex !== -1) {
						client.wallets[existingWalletIndex] = basicWallet;
					} else {
						client.wallets.push(basicWallet);
					}
					
					log.debug(`Created basic wallet for ${chain}`);
					return basicWallet;
				} catch (error) {
					log.error(`Failed to directly create wallet for ${chain}:`, error);
					throw error;
				}
			};
		}
		
		// Enhance connectKeystore method to use directCreateWallet as fallback
		if (typeof client.connectKeystore === 'function') {
			// Save the original method
			const originalConnectKeystore = client.connectKeystore;
			
			// Replace with enhanced version
			client.connectKeystore = async (chains, phrase, index) => {
				try {
					// First try the original method
					log.debug("Attempting original connectKeystore method");
					return await originalConnectKeystore(chains, phrase, index);
				} catch (originalError) {
					log.warn(`Original connectKeystore failed, using direct wallet creation: ${originalError.message}`);
					
					// Fall back to direct creation
					const results = [];
					let successCount = 0;
					
					for (const chain of chains) {
						try {
							// Try direct wallet creation
							log.debug(`Attempting direct wallet creation for ${chain}`);
							const wallet = await client.directCreateWallet(chain, phrase, index);
							if (wallet) {
								results.push({ chain, success: true, wallet });
								successCount++;
								
								// If addWallet exists, register the wallet
								if (typeof client.addWallet === 'function') {
									try {
										await client.addWallet(wallet);
									} catch (e) {
										log.warn(`Failed to register directly created wallet for ${chain}:`, e);
									}
								}
							} else {
								results.push({ chain, success: false, error: "Direct wallet creation returned null" });
							}
						} catch (error) {
							log.error(`Direct wallet creation failed for ${chain}:`, error);
							results.push({ chain, success: false, error: error.toString() });
						}
					}
					
					log.info(`Directly created ${successCount}/${chains.length} wallets`);
					
					if (successCount === 0) {
						throw new Error("Failed to create any wallets directly");
					}
					
					return results;
				}
			};
		}
		
		// Also enhance the getWallet method to use directCreateWallet as fallback
		if (typeof client.getWallet === 'function') {
			// Save original method
			const originalGetWallet = client.getWallet;
			
			// Replace with enhanced version
			client.getWallet = async (chain, phrase, index) => {
				try {
					// First try original method
					log.debug(`Attempting original getWallet for ${chain}`);
					return await originalGetWallet(chain, phrase, index);
				} catch (error) {
					log.warn(`Original getWallet failed for ${chain}, using directCreateWallet: ${error.message}`);
					
					// Try direct wallet creation
					log.debug(`Falling back to directCreateWallet for ${chain}`);
					return await client.directCreateWallet(chain, phrase, index);
				}
			};
		} else if (typeof client.directCreateWallet === 'function') {
			// If no getWallet but directCreateWallet exists, use that
			log.debug("No getWallet method found, using directCreateWallet as getWallet");
			client.getWallet = client.directCreateWallet;
		}
	};

	const setChainflipBroker = useCallback((key, chainflipBroker) => {
		log.debug(`Setting chainflip broker for key ${key}`);
		dispatch({ type: "SET_CHAINFLIPBROKER", key, chainflipBroker });
	}, []);

	const setChainflipToolbox = useCallback((key, chainflipToolbox) => {
		log.debug(`Setting chainflip toolbox for key ${key}`);
		dispatch({ type: "SET_CHAINFLIPTOOLBOX", key, chainflipToolbox });
	}, []);

	const getChainflipToolbox = useCallback(
		async (key, chain) => {
		if (!state.chainflipToolbox || !state.chainflipToolbox[key]) {
			try {
				let keyRing = chain?.cfKeyRing;

				if(!keyRing && chain.signer){
					keyRing = chain.signer;
				}

				const { ChainflipToolbox } = await import(
					/* webpackChunkName: "chainflip-toolbox" */
					"../plugins/substrateToolboxFactory.ts"
				);
				const chainflipToolbox = await ChainflipToolbox({
					providerUrl: "https://chainflip-broker.io:443/rpc/9a3915886f604da7aa0ec45847dc209b",//"https://chainflip.winbit32.com",
					signer: keyRing,
					keyring: keyRing,
					generic: false,
				});

				log.debug("Created chainflip toolbox", chainflipToolbox, keyRing);
				await chainflipToolbox.api.isReady;

				setChainflipToolbox(key, chainflipToolbox);
				return chainflipToolbox;
			} catch (e) {
				log.error("Error creating chainflip toolbox", e);
				throw new Error("Error creating chainflip toolbox");
			}
		}

		return state.chainflipToolbox[key];
	}, [setChainflipToolbox, state.chainflipToolbox]);

	const registerAsBroker = useCallback(async (toolbox) => {
		const extrinsic = toolbox.api.tx.swapping?.registerAsBroker();

		log.debug("Registering as broker", extrinsic);

		if (!extrinsic) {
			return false;
		}

		return await toolbox.signAndBroadcast(extrinsic);
	}, []);

	const chainflipBroker = useCallback(
		async (key, chain) => {
			if (!state.chainflipBroker || !state.chainflipBroker[key]) {
				const chainflipToolbox = await getChainflipToolbox(key, chain);

				log.debug("Creating chainflip broker", chainflipToolbox, chain);
		
				const brokerPubKey = new Uint8Array([158, 110, 87, 118, 81, 171, 252, 12, 204, 174, 206, 219, 228, 26, 8, 230, 38, 189, 11, 212, 184, 247, 209, 83, 39, 161, 127, 35, 39, 204, 82, 4]);
				const brokerAddressRaw = new Uint8Array([158, 110, 87, 118, 81, 171, 252, 12, 204, 174, 206, 219, 228, 26, 8, 230, 38, 189, 11, 212, 184, 247, 209, 83, 39, 161, 127, 35, 39, 204, 82, 4]);
				const networkPrefix = 2112;
				const [{ Keyring }, { ChainflipBroker }] = await Promise.all([
					import(/* webpackChunkName: "polkadot-api" */ "@polkadot/api"),
					import(/* webpackChunkName: "chainflip-broker" */ "../plugins/chainflip/broker.ts"),
				]);
				const brokerKeyRing = new Keyring({ type: "sr25519", ss58Format: networkPrefix }).addFromAddress(brokerAddressRaw, brokerPubKey);
				
				const brokerChain = {cfKeyRing: brokerKeyRing};

				const brokerToolbox = await getChainflipToolbox("broker", brokerChain);

				const chainflipBroker = await ChainflipBroker(brokerToolbox);

				log.debug("Created Chainflip broker", chainflipBroker, brokerToolbox);

				setChainflipBroker(key, chainflipBroker);
				return { broker: chainflipBroker, toolbox: chainflipToolbox };
			}

			return {
				broker: state.chainflipBroker[key],
				toolbox: state.chainflipToolbox[key],
			};
		},
		[getChainflipToolbox, setChainflipBroker, state.chainflipBroker, state.chainflipToolbox, state.wallets]
	);

	// Make sure to expose the tokens and providers that TokenChooserDialog needs
	const tokens = useMemo(() => {
		// Return global tokens from the state
		return state.globalTokens || [];
	}, [state.globalTokens]);

	// Create the context value object to provide to consumers
	const contextValue = useMemo(() => ({
		state,
		dispatch,
		// Recent transactions (in-memory) helpers
		addRecentTx: (key, tx) => dispatch({ type: "ADD_RECENT_TX", key, tx }),
		getRecentTxs: (key) => (state.recentTxs[key] || []),
		clearRecentTxs: (key) => dispatch({ type: "CLEAR_RECENT_TXS", key }),
		disconnect: (key) => {
			log.debug(`Disconnecting client for key ${key}`);
			const client = state.clients[key];
			if (client) {
				try {
					if (typeof client.disconnectAll === 'function') {
						client.disconnectAll();
					}
					// Reset wallets for this key
					dispatch({ type: "RESET_WALLETS", key });
				} catch (error) {
					log.error(`Error disconnecting client for key ${key}:`, error);
				}
			}
		},
		createOrSelectSKClient,
		queueOperation: (key, prop) => {
			return (...args) => {
				// Get the client
				const client = state.clients[key];
				if (!client) {
					log.error(`No client available for key ${key}`);
					return Promise.reject(new Error(`No client available for key ${key}`));
				}
				
				// Check if the property exists on the client
				if (typeof client[prop] !== 'function') {
					log.error(`Method ${prop} not available on client for key ${key}`);
					return Promise.reject(new Error(`Method ${prop} not available on client for key ${key}`));
				}
				
				// Call the method
				return client[prop](...args);
			};
		},
		setWallets: (key, wallets) => {
			log.info(`Setting ${wallets.length} wallets for key ${key}`);
			dispatch({ type: "SET_WALLETS", key, wallets });
		},
		addWallet: (key, wallet) => {
			log.info(`Adding wallet for chain ${wallet.chain} to key ${key}`);
			dispatch({ type: "ADD_OR_UPDATE_WALLET", key, wallet });
		},
		resetWallets: (key) => {
			log.debug(`Resetting wallets for key ${key}`);
			dispatch({ type: "RESET_WALLETS", key });
		},
		setChains: (key, chains) => {
			log.debug(`Setting chains for key ${key}:`, chains);
			dispatch({ type: "SET_CHAINS", key, chains });
		},
		updateWalletBalance: (key, chain, balance) => {
			log.debug(`Updating balance for chain ${chain} in key ${key}:`, balance);
			dispatch({ type: "UPDATE_WALLET_BALANCE", key, chain, balance, timestamp: Date.now() });
		},
		updateWalletAddress: (key, chain, address) => {
			dispatch({ type: "UPDATE_WALLET_ADDRESS", key, chain, address });
		},
		refreshBalance: async (key, chain) => {
			log.debug(`Refreshing balance for chain ${chain} in key ${key}`);
			const client = state.clients[key];
			if (!client) {
				log.error(`No client available for key ${key}`);
				return;
			}
			
			const wallet = state.wallets[key]?.find(w => w.chain === chain);
			if (!wallet) {
				log.error(`No wallet found for chain ${chain} in key ${key}`);
				return;
			}
			
			try {
				// Always fetch a fresh balance from the underlying wallet/toolbox
				// Use cached balance only as a fallback on error
				let balance = [];
				if (typeof wallet.getBalance === 'function') {
					balance = await wallet.getBalance(wallet.address);
				} else {
					log.warn(`Wallet for chain ${chain} has no getBalance method; using cached balance`);
					balance = Array.isArray(wallet.balance) ? wallet.balance : [];
				}
				dispatch({ type: "UPDATE_WALLET_BALANCE", key, chain, balance, timestamp: Date.now() });
				return balance;
			} catch (error) {
				log.error(`Error refreshing balance for chain ${chain} in key ${key}:`, error);
				// Keep previous balance to avoid UI flicker
				dispatch({ type: "UPDATE_WALLET_BALANCE", key, chain, balance: wallet.balance || [], timestamp: Date.now() });
			}
		},
		setChainflipBroker: (key, broker) => {
			log.debug(`Setting chainflip broker for key ${key}:`, broker);
			dispatch({ type: "SET_CHAINFLIPBROKER", key, broker });
		},
		loadProvidersAndTokens,
		tokensLoadedRef,
		loadingTokensPromise,
		chainflipBroker: (key, chain) => chainflipBroker(key, chain),
		getChainflipToolbox,
		setChainflipToolbox,
		registerAsBroker,
		tokens,
		importedTokens: state.importedTokens,
		addImportedToken: (token) => dispatch({ type: "ADD_IMPORTED_TOKEN", token }),
		clearImportedTokens: () => dispatch({ type: "CLEAR_IMPORTED_TOKENS" })
	}), [state, dispatch, createOrSelectSKClient, loadProvidersAndTokens, tokensLoadedRef, chainflipBroker, getChainflipToolbox, setChainflipToolbox, setChainflipBroker, registerAsBroker]);

	const zecRefreshTimerRef = useRef(null);
	const walletsRef = useRef(state.wallets);
	walletsRef.current = state.wallets;

	useEffect(() => {
		if (zecRefreshTimerRef.current) clearInterval(zecRefreshTimerRef.current);

		const refreshZecForAllKeys = () => {
			const allWallets = walletsRef.current || {};
			const keys = Object.keys(allWallets);
			for (const key of keys) {
				const ws = allWallets[key];
				if (!Array.isArray(ws)) continue;
				const zecWallet = ws.find(w => String(w.chain).toUpperCase() === 'ZEC');
				if (zecWallet && typeof zecWallet.getBalance === 'function') {
					zecWallet.getBalance().then(balance => {
						if (Array.isArray(balance) && balance.length > 0) {
							dispatch({ type: "UPDATE_WALLET_BALANCE", key, chain: 'ZEC', balance, timestamp: Date.now() });
						}
					}).catch(() => {});
				}
			}
		};

		zecRefreshTimerRef.current = setInterval(refreshZecForAllKeys, 30000);
		refreshZecForAllKeys();
		return () => {
			if (zecRefreshTimerRef.current) clearInterval(zecRefreshTimerRef.current);
		};
	}, [dispatch]);

	return (
		<SKClientContext.Provider value={contextValue}>
			{children}
		</SKClientContext.Provider>
	);
};

const _NO_CONTEXT_STUB = Object.freeze({
	isClientLoading: true,
	isClientReady: false,
	client: null,
	connect: () => Promise.reject(new Error("Context not available")),
	wallets: EMPTY_WALLETS,
	chains: EMPTY_CHAINS,
	connectChains: [],
	setWallets: () => log.warn("setWallets not available - context missing"),
	resetWallets: () => log.warn("resetWallets not available - context missing"),
	disconnect: () => log.warn("disconnect not available - context missing"),
	addWallet: () => log.warn("addWallet not available - context missing"),
	updateWalletBalance: () => log.warn("updateWalletBalance not available - context missing"),
	updateWalletAddress: () => log.warn("updateWalletAddress not available - context missing"),
	refreshBalance: () => log.warn("refreshBalance not available - context missing"),
	loadTokens: () => Promise.reject(new Error("Context not available")),
	loadProvidersAndTokens: () => Promise.reject(new Error("Context not available")),
	getClient: () => Promise.reject(new Error("Context not available")),
	providers: [],
	chainflipBroker: null,
	tokens: [],
	recentTxs: {},
	addRecentTx: () => {},
	getRecentTxs: () => [],
	clearRecentTxs: () => {}
});

export const useWindowSKClient = (key, windowId) => {
	const contextValue = useContext(SKClientContext);
	
	const {
		state, 
		dispatch, 
		disconnect, 
		createOrSelectSKClient,
		queueOperation,
		setWallets,
		addWallet,
		resetWallets,
		setChains,
		updateWalletBalance,
		updateWalletAddress,
		refreshBalance,
		loadProvidersAndTokens,
		tokensLoadedRef,
		loadingTokensPromise,
		chainflipBroker,
		recentTxs,
		addRecentTx,
		getRecentTxs,
		clearRecentTxs,
		
	} = contextValue || {};
	
	const skClient = state?.clients?.[key] ?? null;
	
	const wallets = state?.wallets?.[key] || EMPTY_WALLETS;
	const chains = state?.chains?.[key] || EMPTY_CHAINS;
	
	const tokens = useMemo(() => {
		return state?.globalTokens || [];
	}, [state?.globalTokens]);

	// Auto-import wallet-held tokens that are popular / CoinGecko-listed so
	// they appear in TokenChooser without opening Portfolio first.
	useEffect(() => {
		if (!contextValue || !wallets?.length) return;
		const known = [
			...(state?.globalTokens || []),
			...(state?.importedTokens || []),
		];
		if (known.length === 0) return;

		let cancelled = false;
		const timer = setTimeout(() => {
			(async () => {
				try {
					const { importKnownWalletTokens } = await import(
						'../apps/winbit32/helpers/walletTokenImport.js'
					);
					if (cancelled) return;
					const count = await importKnownWalletTokens({
						wallets,
						knownTokens: known,
						addImportedToken: (token) => dispatch({ type: 'ADD_IMPORTED_TOKEN', token }),
						requireNonZero: true,
						verifyUnknownViaCoinGecko: true,
					});
					if (count > 0) {
						log.info(`Auto-imported ${count} known wallet token(s) into the main list`);
					}
				} catch (e) {
					log.debug('Wallet token auto-import skipped:', e?.message || e);
				}
			})();
		}, 2500);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [contextValue, wallets, state?.globalTokens, state?.importedTokens, dispatch]);
	

	const waitForTokens = async (maxRetries = 5, retryDelay = 1000) => {
			let retries = 0;
			
			while (retries < maxRetries) {
				// Check if tokens are already loaded
				if (tokensLoadedRef.current || (state.globalTokens && state.globalTokens.length > 1 && state.globalApi)) {
					log.debug("Tokens already loaded");
				
						log.debug("Tokens loaded and API is ready", tokensLoadedRef.current, state.globalTokens?.length);
						if(!tokensLoadedRef.current){
							tokensLoadedRef.current = state.globalTokens;
						}
						return true;
					}

				if (loadingTokensPromise.current) {
					log.debug("Tokens are still loading", loadingTokensPromise.current);
					await loadingTokensPromise.current;
					//wait 1 second before retrying
					await new Promise(resolve => setTimeout(resolve, 3000));
					return waitForTokens();
				}
				
				log.debug(`Tokens not loaded yet, attempt ${retries + 1}/${maxRetries}`);
				
				try {
					// Try to load tokens
					loadingTokensPromise.current = loadProvidersAndTokens();
					await loadingTokensPromise.current;
					
					// Check again after loading attempt
					if (tokensLoadedRef.current || (state.globalTokens && state.globalTokens?.length > 1 && state.globalApi)) {
						log.info("Tokens loaded successfully after retry");
						if(!tokensLoadedRef.current){
							tokensLoadedRef.current = state.globalTokens;
						}
						return true;
					}
				} catch (error) {
					log.warn(`Token loading attempt ${retries + 1} failed:`, error);
				}
				
				// Wait before next retry
				await new Promise(resolve => setTimeout(resolve, retryDelay));
				retries++;
			}
			
			return false;
		};

	// Connect function
	const connect = useCallback(async (phrase, index = 0, chainsOverride) => {
		log.info(`Connecting with phrase for key ${key}`);
		
		try {

			// Wait for tokens to be loaded
			if (!tokensLoadedRef.current || (!state.globalTokens || state.globalTokens?.length < 2 ) ) {
				log.debug("Waiting for tokens to load before connecting");
				await waitForTokens();
			}
			let globalApi = state.globalApi;
			
			// Check if globalApi is a function
			if (!globalApi || typeof globalApi !== 'function') {
				log.error("globalApi is not a function:", typeof state.globalApi, globalApi);
				log.debug("Attempting to recreate the API function");
				

				log.debug("state.globalTokens", state.globalTokens);
				log.debug("tokensLoadedRef.current", tokensLoadedRef.current);

				const tokens = (state.globalTokens && state.globalTokens.length > 1) ? state.globalTokens : tokensLoadedRef.current? tokensLoadedRef.current : [];

				// Try to recreate the API function if possible
				if (tokens.length > 1) {
					try {
						globalApi = balanceApi({
							tokens: tokens,
							apiKey: resolveBalanceApiKey(),
						});

						// Update the state with the recreated API
				dispatch({ 
					type: "SET_GLOBAL_TOKENS", 
							tokens: tokens,
							api: globalApi
						});
						
						// Wait a moment for the state to update
					} catch (apiError) {
						log.error("Failed to recreate API function:", apiError);
					}
				}
			}

			const client = await createOrSelectSKClient(key, globalApi);

			
			if (!client) {
				throw new Error("Failed to create SwapKit client");
			}
			


			// Import the connectWallet function from the client service
			const { connectWallet } = await import('../../services/client');


			// Callers (e.g. the Zcash wizards opened standalone, or
			// Portfolio when the user opens the Radix tab) can narrow
			// or extend the connect set. Without an override we fall
			// through to the default `connectChains` list. With an
			// override we keep anything in either `connectChains` or
			// `optionalChains` so opt-in chains like Radix can be
			// added without bypassing the safety filter entirely.
			const allowedChains = [...connectChains, ...optionalChains];
			let chainsToConnect = Array.isArray(chainsOverride) && chainsOverride.length > 0
				? chainsOverride.filter((c) => allowedChains.includes(c))
				: connectChains;
			if (chainsToConnect.length === 0) chainsToConnect = connectChains;

			try {

				const ps = phrase.split(" ");
				if (ps[0] === "PK") {
					//we have a private key
					const key = ps[1].split(":");
					const keyType = key[0];
					//filter connectChains to only those that support the keyType
					chainsToConnect = connectChains.filter((chain) => {
						const networks = NETWORKS[keyType];
						if (!networks) {
							return false;
						}
						return networks.includes(chain);
					});
					phrase = key[1];

					log.debug("Connecting with private key", keyType, chainsToConnect);
				}

				const result = await connectWallet(client, chainsToConnect, phrase, index, (wallet) => {
					contextValue.addWallet(key, wallet);
				}, windowId || key);

				if (!result.success || !result.wallets || result.wallets.length === 0) {
					throw new Error("Failed to connect any chains");
				}

				// Set wallets and chains in state
				contextValue.setWallets(key, result.wallets);
				contextValue.setChains(key, result.chains);

				return client;
			} catch (error) {
				log.error("Wallet connection failed:", error.message);
				
				// Reset any partial wallets that might have been created
				contextValue.resetWallets(key);
				
				throw error;
			}
		} catch (error) {
			log.error(`Error connecting for key ${key}:`, error);
			throw error;
		}
	}, [key, windowId, state, createOrSelectSKClient, setChains, setWallets, loadProvidersAndTokens, tokensLoadedRef]);
	
	if (!contextValue) {
		log.warn("SKClientContext not available - this may indicate a provider setup issue");
		return _NO_CONTEXT_STUB;
	}

	return {
		skClient,
		isClientLoading: !skClient,
		isClientReady: !!skClient,
		wallets,
		chains,
		tokens: state.globalTokens,
		connect,
		connectChains,
		disconnect: () => contextValue.disconnect(key),
		setWallets: (wallets) => {
			if (typeof contextValue.setWallets === 'function') {
				return contextValue.setWallets(key, wallets);
			} else {
				log.warn("setWallets function not available in context");
			}
		},
		addWallet: (wallet) => {
			if (typeof contextValue.addWallet === 'function') {
				return contextValue.addWallet(key, wallet);
			} else {
				log.warn("addWallet function not available in context");
			}
		},
		resetWallets: () => {
			if (typeof contextValue.resetWallets === 'function') {
				return contextValue.resetWallets(key);
			} else {
				log.warn("resetWallets function not available in context");
			}
		},
		setChains: (chains) => {
			if (typeof contextValue.setChains === 'function') {
				return contextValue.setChains(key, chains);
			} else {
				log.warn("setChains function not available in context");
			}
		},
		updateWalletBalance: (chain, balance) => {
			if (typeof contextValue.updateWalletBalance === 'function') {
				return contextValue.updateWalletBalance(key, chain, balance);
			} else {
				log.warn("updateWalletBalance function not available in context");
			}
		},
		updateWalletAddress: (chain, address) => {
			if (typeof contextValue.updateWalletAddress === 'function') {
				return contextValue.updateWalletAddress(key, chain, address);
			} else {
				log.warn("updateWalletAddress function not available in context");
			}
		},
		refreshBalance: (chain) => {
			if (typeof contextValue.refreshBalance === 'function') {
				return contextValue.refreshBalance(key, chain);
			} else {
				log.warn("refreshBalance function not available in context");
			}
		},
		queueOperation: (prop) => {
			if (typeof contextValue.queueOperation === 'function') {
				return contextValue.queueOperation(key, prop);
			} else {
				log.warn("queueOperation function not available in context");
				return () => Promise.reject(new Error("queueOperation not available"));
			}
		},
		providers: state.providers,
		chainflipBroker: (chain) => contextValue.chainflipBroker(key, chain),
		importedTokens: state.importedTokens,
		addImportedToken: (token) => dispatch({ type: "ADD_IMPORTED_TOKEN", token }),
		clearImportedTokens: () => dispatch({ type: "CLEAR_IMPORTED_TOKENS" }),
		recentTxs,
		addRecentTx: (tx) => addRecentTx(key, tx),
		getRecentTxs: () => getRecentTxs(key),
		clearRecentTxs: () => clearRecentTxs(key),
		// Add API methods for Zcash functionality
		api: {
			// Note: Unified address generation has been moved to ZCashWallet.jsx using WebZjs WASM
			// This ensures proper WASM-based generation that passes RPC validation
			generateZcashShieldedAddress: async () => {
				log.debug('Unified address generation is handled by ZCashWallet.jsx using WebZjs WASM');
				return {
					success: false,
					shieldedAddress: '',
					message: 'Unified address generation must be done through the ZCash Wallet UI using WebZjs WASM'
				};
			},
			
			// Check if a z-address is imported to the node
			checkZcashAddressImported: async (address) => {
				log.debug('API: checkZcashAddressImported called for address:', address);
				try {
					const zcashWallet = wallets.find(wallet => wallet.chain === ExtendedChain.Zcash);
					if (!zcashWallet) {
						throw new Error('No Zcash wallet found');
					}
					
					if (zcashWallet.checkAddressImported && typeof zcashWallet.checkAddressImported === 'function') {
						return await zcashWallet.checkAddressImported(address);
					}
					
					log.warn('No address import check method available in wallet');
					return { isImported: false, hasViewingKey: false };
				} catch (error) {
					log.error('Error checking address import status:', error);
					return { isImported: false, hasViewingKey: false };
				}
			},
			
			// Import a z-address to the node
			importZcashAddress: async (address, viewingKey) => {
				log.debug('API: importZcashAddress called for address:', address);
				try {
					const zcashWallet = wallets.find(wallet => wallet.chain === ExtendedChain.Zcash);
					if (!zcashWallet) {
						throw new Error('No Zcash wallet found');
					}
					
					if (zcashWallet.importAddress && typeof zcashWallet.importAddress === 'function') {
						return await zcashWallet.importAddress(address, viewingKey);
					}
					
					log.warn('No address import method available in wallet');
					return { success: false, message: 'Address import functionality not available' };
				} catch (error) {
					log.error('Error importing address:', error);
					return { success: false, message: error.message };
				}
			}
		},
		// Expose client creation function for proactive initialization
		createOrSelectSKClient: async (api) => {
			// Ensure we end up with a callable API function
			let effectiveApi = api;
			if (!effectiveApi || typeof effectiveApi !== 'function') {
				effectiveApi = state.globalApi;
			}
			
			// If still not a function, attempt to load providers/tokens to populate globalApi
			if (!effectiveApi || typeof effectiveApi !== 'function') {
				log.debug('No API available for client creation, attempting to load providers and tokens...');
				try {
					await loadProvidersAndTokens();
				} catch (error) {
					log.warn('Failed to load providers and tokens for client initialization:', error);
				}
				effectiveApi = state.globalApi;
			}
			
			// Final fallback: construct a minimal balance API from whatever tokens we have
			if (!effectiveApi || typeof effectiveApi !== 'function') {
				const tokens = Array.isArray(state.globalTokens) && state.globalTokens.length > 0
					? state.globalTokens
					: (tokensLoadedRef.current && Array.isArray(tokensLoadedRef.current)) ? tokensLoadedRef.current : [];
				log.debug('Creating minimal balanceApi fallback with tokens:', tokens.length);
				try {
					effectiveApi = balanceApi({
						tokens,
						apiKey: resolveBalanceApiKey(),
					});
					// Persist into state for subsequent calls
					dispatch({ type: 'SET_GLOBAL_TOKENS', tokens, api: effectiveApi });
				} catch (e) {
					log.error('Failed to create balanceApi fallback:', e);
				}
			}
			
			if (typeof effectiveApi !== 'function') {
				throw new Error('Unable to initialize a valid API function for client creation');
			}
			
			return createOrSelectSKClient(key, effectiveApi);
		}
	};
};
